import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  updateCaseHeader,
  getDetailedHistoryCoalesced,
  getCustomerDetailedHistoryCoalesced,
} from "requests/homeowner/caseforms";
import { MSpinner, MCard, MDialog, ACTION_CLOSE } from "components/Shared";
import { getFRSortFunc, hasCustomerRight, getCaseType } from "components/Shared/Functions";
import Grid from "@mui/material/Grid";
import { connect } from "react-redux";
import { getCaseHeaderV2Redux } from "actions/homeowner/caseforms";
import { caseHeaderV2Selector } from "selectors/homeowner/caseForms/caseHeaderV2";
import CaseHazards from "components/QualityAssurance/Sections/Outline/CaseHazards";
import {
  CASE_HEADER_AGENT_CODE_EDIT,
  ASSIGN,
  CASE_HEADER_RIGHT_FILTERED_VIEW,
  CASE_PROPERTY_HISTORY_VIEW,
  CASE_PROPERTY_HISTORY_VIEW_LIMITED,
  CASE_HISTORY_CALL_LOG_VIEW,
  CASE_HISTORY_CARD_VIEW,
  CASE_HISTORY_INTERNAL_NOTES_VIEW,
  APPRAISAL_ASSIGNMENT_APPROVAL_EDIT,
  APPRAISAL_ASSIGNMENT_APPROVAL_VIEW,
  CASE_FR_SCHEDULE_VIEW,
  CASE_APPRAISER_SCHEDULE_VIEW,
  FIELD_SCHEDULING_REVIEW,
  FIELD_SCHEDULING_MANAGE,
  QA_CASE_AUDIT_VIEW,
  RELATED_CASES_VIEW,
} from "typeConstants/rights";
import { DATA_TYPE_JSX, DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import AgentSearch from "components/QualityAssurance/Agent/AgentSearch";
import { cloneDeep } from "lodash";
import { userHasRight, userHasAnyRight } from "selectors/rights";
import { getMinimalInspectorListByCase } from "actions/homeowner/fieldReps";
import { assignCasesToInspector, unassignCases } from "actions/assignment";
import { getAppraiserListByAppraisalId } from "actions/appraiser";
import AssignedAppraiserAuditPopup from "components/QualityAssurance/Sections/CaseDetails/AssignedAppraiserAuditPopup";
import { changeAppraiserAssigned } from "actions/homeowner/casedetails";
import responseHandler from "responseHandler";
import PublicPropertyRecord from "components/Homeowner/CaseForms/PublicPropertyRecord";
import { InspectionComments } from "components/QualityAssurance";
import PropertyCaseHistory from "components/Homeowner/CaseForms/PropertyCaseHistory";
import { getCaseLinksRedux, getCaseLinksLimitedRedux } from "requests/homeowner/ho/index";
import CompactHistory from "components/Homeowner/History/CompactHistory";
import CallLogs from "components/Homeowner/ContactEfforts/CallLogs.tsx";
import { NotificationStep } from "components/Notifications";
import AppraiserAssignmentApproval from "components/Homeowner/CaseForms/AppraiserAssignmentApproval";
import CaseRecommendations from "components/QualityAssurance/Sections/Outline/CaseRecommendations";
import FieldRepAvailabilityView, {
  frScheduleStatusList,
} from "components/Homeowner/CaseForms/FieldRepAvailabilityView.tsx";
import AppraiserAvailabilityView, {
  AppraiserAvailabilityViewStatusCodes,
} from "components/Homeowner/CaseForms/AppraiserAvailabilityView";
import { caseTypes } from "components/Utility/Objects";
import { SecondaryStatus } from "typeConstants/caseStatusCodes";
import SendCaseToUserQueue from "components/CallCenter/SharedComponents/SendCaseToUserQueue";
import QaCaseAuditWarning from "components/CaseAudits/QaCaseAuditWarning";
import CaseFollowUp from "components/Homeowner/CaseForms/CaseFollowUp.tsx";
import RelatedCases from "components/Homeowner/CaseForms/RelatedCases.tsx";
import AppointmentGrid, {
  validStatusesForAppointmentGrid,
} from "components/CallCenter/AppointmentGrid/AppointmentGrid.tsx";
import LimitedCaseView from "components/Homeowner/CaseForms/CaseHeaderV2/LimitedCaseView.tsx";
import AppointmentConfirmation from "components/CallCenter/AppointmentGrid/AppointmentConfirmation.tsx";
import { sendConfirmation } from "requests/caseAppointment/index.tsx";
import ComparisonChart from "components/QualityAssurance/Sections/Outline/ComparisonChart.tsx";
import CaseAttributesCard from "components/QualityAssurance/Sections/CaseDetails/CaseAttributesCard.tsx";
import { createRequestCoalescer } from "tools/requestCoalescer";

const fieldRepListDispatchCoalescer = createRequestCoalescer();

const CaseHeaderV2 = (props) => {
  const mounted = useRef(true);
  const historyRequestCleanup = useRef(() => {});
  const newAppraiser = useRef(null);
  const cardEditObj = useRef(null);
  const appointmentChanged = useRef(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showAppraiserDialog, setShowAppraiserDialog] = useState(false);
  const [showApptConfirmDialog, setShowApptConfirmDialog] = useState(false);
  const [needAssignmentComment, setNeedAssignmentComment] = useState(false);
  const [needProximityComment, setNeedProximityComment] = useState(false);
  const [needLicenseComment, setNeedLicenseComment] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [history, setHistory] = useState();
  const showCallLogCard = props.canViewCallLog && (props.fieldSchedulingReview || props.verticalDisplay);
  const requestedControlNumber = Number(props.controlNumber);
  const loadedControlNumber = Number(props.caseHeaderV2?.caseHeaderDto?.caseInfo?.controlNumber);
  const caseHeaderV2 =
    !Number.isNaN(loadedControlNumber) && loadedControlNumber === requestedControlNumber
      ? props.caseHeaderV2
      : null;

  useEffect(() => {
    if (props.controlNumber) {
      if (!props.fetchingCaseHeaderV2 && !caseHeaderV2) {
        props.getCaseHeaderV2Redux(props.controlNumber);
        if (showCallLogCard) getHistory();
        if (!props.fetchingCaseLinks) {
          if (props.canViewCasePropertyHistory) {
            props.getCaseLinks(props.controlNumber);
          } else if (props.canViewCasePropertyHistoryLimited) {
            props.getCaseLinksLimited(props.controlNumber);
          }
        }
      }
      if (props.canAssign) {
        fieldRepListDispatchCoalescer.run(
          props.controlNumber,
          (resolve) => {
            props.getFRList(props.controlNumber);
            resolve();
          },
          () => {},
        );
      }
    }
  }, [props.controlNumber]);

  useEffect(() => {
    if (caseHeaderV2?.caseHeaderDto?.caseInfo?.appraisalSurveyInfo && props.canAssign && !props.isFetchingAssignableAppraisers) {
      props.getAppraiserListByAppraisalId(props.controlNumber);
    }
    if (appointmentChanged.current) {
      // If the user has the right and there's an assigned FR after appt change, show send appt confirm dialog
      if (props.fieldSchedulingReview && caseHeaderV2?.caseHeaderDto?.caseInfo?.inspectorNumber) {
        // This shouldn't be shown for fake FRs. Not sure if we should add isFakeFR to CaseInfo or do it here when we probably have the data
        const assignedFR = props.FRList?.find(
          (fr) => fr.number === Number(caseHeaderV2?.caseHeaderDto?.caseInfo?.inspectorNumber),
        );
        if (assignedFR && !assignedFR.isFake) {
          setShowApptConfirmDialog(true);
        }
      }
      appointmentChanged.current = false;
    }
  }, [caseHeaderV2]);

  useEffect(() => () => historyRequestCleanup.current(), []);

  const isLimitedView = () => caseHeaderV2?.caseHeaderDto?.caseInfo?.limitedView?.isLimitedView;

  const handleSave = (arr, obj, fullRefresh = false) => {
    // If FR or Appraiser were changed, we need to use different endpoints
    // These 2 functions will call handleSave again for any remaining changes
    if (Object.keys(obj).includes("appraiserAssigned")) {
      // Skip if the appraiser wasn't actually changed
      if (obj.appraiserAssigned?.appraiserNumber !== caseHeaderV2?.caseHeaderDto?.caseInfo?.appraiserNumber) {
        handleAppraiserEdit(obj);
        return;
      } else {
        delete obj.appraiserNumber;
      }
    }
    // Need to set this after appraiser change since we may need to show the dialog
    setIsUpdating(true);
    if (Object.keys(obj).includes("inspectorNumber")) {
      // Skip if the FR wasn't actually changed
      if (obj.inspectorNumber !== caseHeaderV2?.caseHeaderDto?.caseInfo?.inspectorNumber) {
        handleInspectorEdit(obj);
        return;
      } else {
        delete obj.inspectorNumber;
      }
    }
    // Need to remove timezone offsets from appointment dates
    // Otherwise appointments made by users outside of the case's time zone may appear incorrect
    if (Object.keys(obj).includes("CaseInfo.AppointmentDateTime") && obj["CaseInfo.AppointmentDateTime"]) {
      obj["CaseInfo.AppointmentDateTime"] = obj["CaseInfo.AppointmentDateTime"].slice(0, 19);
      if (props.fieldSchedulingReview) {
        // After saving, we want to show the appointment confirmation dialog
        appointmentChanged.current = true;
      }
    }
    if (
      Object.keys(obj).includes("CaseInfo.AppraisalSurveyInfo.AppraisalAppointmentDate") &&
      obj["CaseInfo.AppraisalSurveyInfo.AppraisalAppointmentDate"]
    ) {
      obj["CaseInfo.AppraisalSurveyInfo.AppraisalAppointmentDate"] = obj[
        "CaseInfo.AppraisalSurveyInfo.AppraisalAppointmentDate"
      ].slice(0, 19);
    }

    updateCaseHeader(
      props.controlNumber,
      obj,
      () => {
        if (fullRefresh === true && props.refreshMethod) {
          props.refreshMethod();
        } else {
          props.getCaseHeaderV2Redux(props.controlNumber);
        }
        cardEditObj.current = null;
        setIsUpdating(false);
      },
      mounted,
    );
  };

  const handleInspectorEdit = (obj) => {
    let hasOtherChanges = Object.keys(obj).length > 1; // Any changes other than inspectorNumber
    const currentFrNumber = caseHeaderV2?.caseHeaderDto?.caseInfo?.inspectorNumber;
    const newFrNumber = obj.inspectorNumber;

    delete obj.inspectorNumber; // Delete to prevent looping in handleSave and keep it out of the call to updateCaseHeader
    let isReassigned = currentFrNumber > 0;
    let callBack = hasOtherChanges
      ? () => handleSave(null, obj)
      : () => {
          props.getCaseHeaderV2Redux(props.controlNumber);
          setIsUpdating(false);
        };
    if (!hasOtherChanges) setIsUpdating(true);
    if (!isReassigned) {
      // Unassigned case has been assigned
      props.assignCasesToInspector([props.controlNumber], newFrNumber, callBack);
    } else if (newFrNumber === undefined) {
      // Assigned case has been unassigned
      props.unassignCasesFromInspector([props.controlNumber], callBack);
    } else {
      // Reassigned case
      props.unassignCasesFromInspector([props.controlNumber], () =>
        props.assignCasesToInspector([props.controlNumber], newFrNumber, callBack),
      );
    }
  };

  const handleAppraiserEdit = (obj) => {
    newAppraiser.current = obj.appraiserAssigned;
    delete obj.appraiserAssigned; // Delete to prevent looping in handleSave and keep it out of the call to updateCaseHeader
    // Hold on to these changes so we can call handleSave after dialog dismissed
    cardEditObj.current = obj;

    if (props.assignableAppraisers?.assignmentReasonFeature || props.assignableAppraisers?.proximityCommentFeature) {
      let newlyAssignedAppraiserId = newAppraiser.current?.appraiserId;
      let selectedAppraiser = props.assignableAppraisers?.appraisersAvailable?.find(
        (aa) => aa.id === newlyAssignedAppraiserId,
      );
      let needProximityComment =
        props.assignableAppraisers?.proximityCommentFeature && Number(selectedAppraiser?.proximityToSubject) > 150; // Miles
      let needAssignmentComment = false;
      if (selectedAppraiser && props.assignableAppraisers?.assignmentReasonFeature) {
        let cmp = (x, y) => (x > y) - (x < y);
        let sortedGrades = props.assignableAppraisers?.appraisersAvailable
          ?.map((aa) => aa.performanceGrade)
          .sort((x, y) => cmp(x, y));
        if (sortedGrades.length > 1) {
          const highestGrade = sortedGrades.shift();
          needAssignmentComment =
            (sortedGrades.some((g) => g === highestGrade) && highestGrade === selectedAppraiser.performanceGrade) ||
            highestGrade !== selectedAppraiser.performanceGrade;
        }
      }
      // This value will be null unless the license is expiring in the next 10 days and then a comment is required
      let needLicenseExpiringComment = selectedAppraiser && selectedAppraiser?.licenseExpiringSoonDate !== null;
      if (needProximityComment || needAssignmentComment || needLicenseExpiringComment) {
        setShowAppraiserDialog(true);
        setNeedProximityComment(needProximityComment);
        setNeedAssignmentComment(needAssignmentComment);
        setNeedLicenseComment(needLicenseExpiringComment);
        return;
      }
    }
    newAppraiser.current
      ? updateAppraiser() // Use default reason
      : updateAppraiser(null); // No reason
  };

  const updateAppraiser = (
    option = "Highest rated appraiser selected",
    assignComment = null,
    proximityComment = null,
    licenseComment = null,
  ) => {
    let payload = {
      noteBody: null,
      isExternal: false,
    };
    let addToNoteBody = (note) =>
      payload.noteBody?.length ? (payload.noteBody += `; ${note}`) : (payload.noteBody = note);
    if (option) {
      if (option === "Highest rated appraiser selected") {
        payload.noteBody = option;
      } else {
        payload.noteBody = `Assignment Reason: ${option !== "Other" ? option : assignComment}`;
        props.makeSuccessToast("Appraiser assignment details submitted.");
      }
    }
    if (proximityComment) {
      addToNoteBody(`Assignment over 150 mi: ${proximityComment}`);
    }
    if (licenseComment) {
      addToNoteBody(`Assignment w/ license expiring in 10 days: ${licenseComment}`);
    }
    if (option || proximityComment || licenseComment) {
      setShowAppraiserDialog(false);
      setNeedProximityComment(false);
      setNeedAssignmentComment(false);
      setNeedLicenseComment(false);
    }
    let hasOtherChanges = Object.keys(cardEditObj.current).length > 0;
    const handleAssignmentResults = (assignmentResults) => {
      if (assignmentResults?.caseAssignmentDetails?.length) {
        // show toast message to the user if assignment details were returned
        const toastMessage = assignmentResults.caseAssignmentDetails
          .map((e) => `${e.caseId} ${e.message}`)
          .join("\n")
          .trim();
        if (toastMessage) {
          props.makeSuccessToast(toastMessage);
        }
      }
    };
    let callBack = hasOtherChanges
      ? (assignmentResults) => {
          handleAssignmentResults(assignmentResults);
          handleSave(null, cardEditObj.current);
        }
      : (assignmentResults) => {
          handleAssignmentResults(assignmentResults);
          props.getCaseHeaderV2Redux(props.controlNumber);
          setIsUpdating(false);
        };
    if (!hasOtherChanges) setIsUpdating(true);
    let currentAppraiser = caseHeaderV2?.caseHeaderDto?.caseInfo?.appraiserNumber;
    let caseAssignmentDetails = {
      caseId: props.controlNumber,
      approvalRequestComments: proximityComment,
      caseNoteBody: payload.noteBody,
    };
    if (newAppraiser.current && !currentAppraiser) {
      // Unassigned case is assigned
      props.assignCasesToAppraiser([props.controlNumber], newAppraiser.current.appraiserNumber, callBack, [
        caseAssignmentDetails,
      ]);
    } else if (!newAppraiser.current && currentAppraiser) {
      // Assigned case is unassigned
      props.unassignCasesFromAppraiser([props.controlNumber], callBack);
    } else {
      // Assignment changed
      props.changeAppraiserAssigned(
        props.controlNumber,
        newAppraiser.current.appraiserId,
        callBack,
        caseAssignmentDetails,
      );
    }
    newAppraiser.current = null;
  };

  let cards;
  let isLoaded = caseHeaderV2 && !props.fetchingCaseHeaderV2 && !props.fetchingFRList;
  const isLenderCase =
    getCaseType(caseHeaderV2?.caseHeaderDto?.policyInfo?.customerInfo?.customerType) === caseTypes.valuation;

  if (isLoaded) {
    cards = caseHeaderV2?.cards
      .map((card) => {
      let newCard = { ...card };
      let addAgentEdit = props.showAgentEdit && card.fields.some((f) => f.key === "PolicyInfo.CaseAgentInfo.AgentName");
      let addFREdit = props.canAssign && card.fields.some((f) => f.key === "CaseInfo.AssignedTo");
      let addAppraiserEdit = props.canAssign && card.fields.some((f) => f.key === "CaseInfo.AppraiserAssigned");
      if (addAgentEdit || addFREdit || addAppraiserEdit) {
        newCard = cloneDeep(card); // Copy the object so we arent editing the props
      }
      // Add agent edit
      if (addAgentEdit) {
        let agentIndex = card.fields.findIndex((f) => f.key === "PolicyInfo.CaseAgentInfo.AgentName");
        let agentName = newCard.fields[agentIndex].value;
        newCard.fields[agentIndex].value = (
          <>
            {agentName}
            <i
              onClick={() => setShowAgentDialog(true)}
              className="md-icon material-icons edit-agent-icon"
              title={isLenderCase ? "Edit Lender" : "Edit Agent"}
            >
              edit
            </i>
          </>
        );
        newCard.fields[agentIndex].dataType = DATA_TYPE_JSX;
      }
      // Add FR edit
      if (addFREdit) {
        let frIndex = card.fields.findIndex((f) => f.key === "CaseInfo.AssignedTo");
        let frName = caseHeaderV2?.caseHeaderDto?.caseInfo?.assignedTo;
        let frNumber = caseHeaderV2?.caseHeaderDto?.caseInfo?.inspectorNumber;
        newCard.fields[frIndex].editFields = [
          {
            key: "inspectorNumber",
            label: "Field Rep",
            dataType: DATA_TYPE_DROPDOWN2,
            value: frName && frNumber ? { label: frName, value: frNumber } : null,
            dataTypeOptions: {
              options: props.FRList?.map((x) => ({ label: x.fullName, value: x.number })),
              nullable: true,
              sortFunc: getFRSortFunc(),
            },
            editable: ![SecondaryStatus.COMPLETED, SecondaryStatus.QA].includes(
              caseHeaderV2?.caseHeaderDto.caseInfo.webStatus,
            ),
          },
        ];
      }
      // Add Appraiser edit
      if (addAppraiserEdit) {
        let appraiserIndex = card.fields.findIndex((f) => f.key === "CaseInfo.AppraiserAssigned");
        let appraiserName = caseHeaderV2?.caseHeaderDto?.caseInfo?.appraiserAssigned;
        let appraiserNumber = caseHeaderV2?.caseHeaderDto?.caseInfo?.appraiserNumber;
        let appraiserId = caseHeaderV2?.caseHeaderDto?.caseInfo?.appraiserId;
        let allowsEdits = caseHeaderV2?.caseHeaderDto?.caseInfo?.appraiserAllowsEdits;
        let accountFeatures =
          props.assignableAppraisers?.assignmentReasonFeature || props.assignableAppraisers?.proximityCommentFeature;
        if (appraiserName) {
          // This field uses appraiserFormatter to display additional icons
          newCard.fields[appraiserIndex].value = `${appraiserName}/${allowsEdits}/${accountFeatures}`;
        }
        newCard.fields[appraiserIndex].editFields = [
          {
            key: "appraiserAssigned",
            label: "Appraiser",
            dataType: DATA_TYPE_DROPDOWN2,
            value:
              appraiserName && appraiserNumber && appraiserId
                ? { label: appraiserName, value: { appraiserNumber, appraiserId } }
                : null,
            dataTypeOptions: {
              options: props.assignableAppraisers?.appraisersAvailable?.map((x) => {
                return {
                  label:
                    (x.isSalary ? "S-" : "") +
                    x.fullName +
                    ` (${x.performanceGrade}; ${x.proximityToSubject} mi` +
                    (x.licenseExpiringSoonDate ? `; Lic Exp: ${x.licenseExpiringSoonDate}` : "") +
                    ")",
                  value: { appraiserNumber: x.appraiserNumber, appraiserId: x.id },
                };
              }),
            },
            editable: caseHeaderV2?.caseHeaderDto?.caseInfo?.appraisalSurveyInfo?.canChangeAppraiserAssignment,
          },
        ];
      }
      return newCard;
    });
  }

  const getHistoryControl = () => {
    return (
      <CompactHistory
        controlNumber={props.controlNumber}
        historyData={showCallLogCard ? history : null}
        refreshCallback={showCallLogCard ? getHistory : null}
        viewInternalNotesInput={props.viewInternalNotesInput}
      />
    );
  };

  const getHistory = () => {
    // This is going to need to be refactored in the near future. It is dangerous to default to the full history detail and only display the modified version if they are a customer
    // The reason for not fixing it now is we don't have a full list of roles that use the full detailed history and a larger discussion/task is needed to discuss how rights are going to drive the display of components
    const getHistoryRequest = hasCustomerRight(props.rights)
      ? getCustomerDetailedHistoryCoalesced
      : getDetailedHistoryCoalesced;

    historyRequestCleanup.current();
    historyRequestCleanup.current = getHistoryRequest(
      props.controlNumber,
      (data) => {
        if (mounted.current) {
          setHistory(data);
        }
      },
    );
  };

  const refreshCaseHeaderV2 = () => props.getCaseHeaderV2Redux(props.controlNumber);

  const frScheduleViewNeeded = frScheduleStatusList.includes(caseHeaderV2?.caseHeaderDto.caseInfo.webStatus);
  const appraiserScheduleViewNeeded =
    isLenderCase &&
    AppraiserAvailabilityViewStatusCodes.includes(
      caseHeaderV2?.caseHeaderDto?.caseInfo?.appraisalSurveyInfo?.workflowStatusCode,
    );

  const appointmentGridNeeded = validStatusesForAppointmentGrid.includes(
    caseHeaderV2?.caseHeaderDto.caseInfo.webStatus,
  );

  if (isLimitedView()) {
    // refresh method is null if we are mounted in quick lists, so we just call the more limited refresh since we are in a limited quick list view anyways
    return (
      <LimitedCaseView
        caseHeader={caseHeaderV2}
        cards={cards}
        refreshMethod={props.refreshMethod ?? refreshCaseHeaderV2}
        handleSave={(arr, map) => handleSave(arr, map, true)}
      />
    );
  }

  const closeApptConfirmDialog = () => setShowApptConfirmDialog(false);
  const onSendConfirmation = (contacts) => {
    if (contacts?.length > 0) {
      const onSuccess = () => props.makeSuccessToast("Appointment Confirmation queued to send");
      sendConfirmation(props.controlNumber, contacts, onSuccess, mounted);
    }
    setShowApptConfirmDialog(false);
  };

  return !isLoaded || isUpdating ? (
    <MSpinner />
  ) : (
    <>
      <Grid
        container
        spacing={1}
        sx={{ padding: 1, alignItems: "stretch" }}
      >
        {props.fieldSchedulingManage && props.quickListDisplay && (
          <Grid size={12}>
            <SendCaseToUserQueue controlNumber={props.controlNumber} />
          </Grid>
        )}
        {showAppraiserDialog && (
          <AssignedAppraiserAuditPopup
            submitAssignedAppraiserReason={updateAppraiser}
            proximityCommentNeeded={needProximityComment}
            assignedReasonNeeded={needAssignmentComment}
            licenseCommentNeeded={needLicenseComment}
          />
        )}
        {props.fieldSchedulingReview && appointmentGridNeeded && (
          <Grid size={12}>
            <MCard title="Top 5 Available Appointments">
              <AppointmentGrid controlNumber={props.controlNumber} refreshParent={refreshCaseHeaderV2} />
            </MCard>
          </Grid>
        )}
        {props.showFRScheduleView && frScheduleViewNeeded && (
          <Grid size={12}>
            <FieldRepAvailabilityView controlIdNumber={props.controlNumber} />
          </Grid>
        )}
        {props.showAppraiserScheduleView && appraiserScheduleViewNeeded && (
          <Grid size={12}>
            <AppraiserAvailabilityView controlIdNumber={props.controlNumber} />
          </Grid>
        )}
        {props.hasCaseAuditViewPermission && (
          <Grid size={12}>
            <QaCaseAuditWarning controlNumber={props.controlNumber} />
          </Grid>
        )}
        {cards.map((c) => {
          // Display Call Log in Case Header if scheduling view
          if (c.key === "call-log") {
            if (showCallLogCard) {
              return (
                <Grid size={props.verticalDisplay ? 12 : 6} key={c.key}>
                  <MCard
                    title="Call Log"
                    className="quicklist-calllog"
                  >
                    <CallLogs
                      controlNumber={props.controlNumber}
                      tableData={history?.callLogs}
                      refreshCallback={getHistory}
                    />
                  </MCard>
                </Grid>
              );
            }
          } else if (c.key === "next-notification") {
            if (props.fieldSchedulingReview) {
              return (
                <Grid size={props.verticalDisplay ? 12 : 6} key={c.key}>
                  <MCard
                    title={"Next Notification"}
                    className={"quicklist-calllog"}
                  >
                    <NotificationStep controlNumber={props.controlNumber} />
                  </MCard>
                </Grid>
              );
            }
          } else {
            return (
              <Grid size={props.verticalDisplay ? 12 : 6} key={c.key}>
                <MCard
                  {...c}
                  onEditFinished={handleSave}
                />
              </Grid>
            );
          }
        })}
        {props.showAppraiserApprovalCard && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <AppraiserAssignmentApproval
              controlNumber={props.controlNumber}
            />
          </Grid>
        )}
        {props.showPublicPropertyCard && !props.fieldSchedulingReview && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <PublicPropertyRecord
              controlNumber={Number(caseHeaderV2.caseHeaderDto.caseInfo.controlNumber)}
            />
          </Grid>
        )}
        {props.editAndShowInspectionComments && !props.fieldSchedulingReview && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <InspectionComments
              controlNumber={Number(caseHeaderV2.caseHeaderDto.caseInfo.controlNumber)}
              canUserEdit={props.editAndShowInspectionComments}
              text={caseHeaderV2.caseHeaderDto.policyInfo?.surveyRemarks}
            />
          </Grid>
        )}
        {(props.canViewCasePropertyHistory || props.canViewCasePropertyHistoryLimited) && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <PropertyCaseHistory
              caseLinks={props.caseLinks}
              fetchingCaseLinks={props.fetchingCaseLinks}
            />
          </Grid>
        )}
        {props.showRelatedCases && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <RelatedCases
              controlNumber={props.controlNumber}
            />
          </Grid>
        )}
        {caseHeaderV2.caseHeaderDto.caseInfo.hasHazards && !props.fieldSchedulingReview && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <CaseHazards
              controlNumber={props.controlNumber}
            />
          </Grid>
        )}
        {caseHeaderV2.caseHeaderDto.caseInfo.hasRecommendations && (
          <Grid size={12}>
            <CaseRecommendations controlNumber={props.controlNumber} />
          </Grid>
        )}
        {caseHeaderV2.caseHeaderDto.caseInfo.hasComparisonFields && (
          <Grid size={12}>
            <ComparisonChart controlNumber={props.controlNumber} />
          </Grid>
        )}
        {props.viewCaseHistoryCard && (
          <Grid size={props.verticalDisplay ? 12 : 6}>
            <MCard title="History">
              {getHistoryControl()}
            </MCard>
          </Grid>
        )}
        <Grid size={props.verticalDisplay ? 12 : 6} sx={{ maxHeight: "300px" }}>
            <CaseAttributesCard controlNumber={props.controlNumber} gridHeightPixels={300} />
          </Grid>
        <Grid size={props.verticalDisplay ? 12 : 6}>
          <CaseFollowUp
            controlNumber={props.controlNumber}
            internetUserId={props.internetUserId}
          />
        </Grid>
      </Grid>
      {showAgentDialog && (
        <MDialog
          id="agent-edit-dialog"
          title={isLenderCase ? "Lender Details" : "Agent Details"}
          actions={[{ code: ACTION_CLOSE, onClick: () => setShowAgentDialog(false) }]}
          sxPaper={{ width: "800px", maxHeight: "800px", }}
        >
          <AgentSearch
            controlIDNumber={props.controlNumber}
            onAgentSelected={(agent) => {
              setShowAgentDialog(false);
              updateCaseHeader(
                props.controlNumber,
                {
                  "PolicyInfo.CaseAgentInfo.Code": agent.agentCode,
                  "PolicyInfo.CaseAgentInfo.AgentName": agent.agentName,
                  "PolicyInfo.CaseAgentInfo.AgentPhone": agent.phoneNumber,
                  "PolicyInfo.CaseAgentInfo.AgentId": agent.id,
                },
                refreshCaseHeaderV2,
                mounted,
              );
            }}
          />
        </MDialog>
      )}
      {props.fieldSchedulingReview && showApptConfirmDialog && (
        <MDialog id="appointment-confirm-dialog" title="Send Appointment Confirmation?">
          <AppointmentConfirmation
            onSave={onSendConfirmation}
            onCancel={closeApptConfirmDialog}
            saveButtonLabel="Send"
          />
        </MDialog>
      )}
    </>
  );
};

CaseHeaderV2.propTypes = {
  controlNumber: PropTypes.number.isRequired, // Control number of the case
  verticalDisplay: PropTypes.bool, // If true, will display in a single column
  refreshMethod: PropTypes.func.isRequired,
  // State
  caseHeaderV2: PropTypes.object,
  fetchingCaseHeaderV2: PropTypes.bool,
  showAgentEdit: PropTypes.bool,
  canAssign: PropTypes.bool,
  FRList: PropTypes.array,
  fetchingFRList: PropTypes.bool,
  showPublicPropertyCard: PropTypes.bool,
  editAndShowInspectionComments: PropTypes.bool,
  canViewCasePropertyHistory: PropTypes.bool,
  canViewCasePropertyHistoryLimited: PropTypes.bool,
  showRelatedCases: PropTypes.bool,
  viewCaseHistoryCard: PropTypes.bool,
  viewInternalNotesInput: PropTypes.bool,
  caseLinks: PropTypes.array,
  fetchingCaseLinks: PropTypes.bool,
  fieldSchedulingReview: PropTypes.bool,
  fieldSchedulingManage: PropTypes.bool,
  canViewCallLog: PropTypes.bool,
  showAppraiserApprovalCard: PropTypes.bool,
  showFRScheduleView: PropTypes.bool,
  showAppraiserScheduleView: PropTypes.bool,
  internetUserId: PropTypes.number,
  // Dispatch
  getCaseHeaderV2Redux: PropTypes.func,
  getFRList: PropTypes.func,
  assignCasesToInspector: PropTypes.func,
  assignCasesToAppraiser: PropTypes.func,
  unassignCasesFromInspector: PropTypes.func,
  unassignCasesFromAppraiser: PropTypes.func,
  isFetchingAssignableAppraisers: PropTypes.bool,
  assignableAppraisers: PropTypes.object,
  makeSuccessToast: PropTypes.func,
  makeWarningToast: PropTypes.func,
  getCaseLinks: PropTypes.func,
  getCaseLinksLimited: PropTypes.func,
  quickListDisplay: PropTypes.bool,
  hasCaseAuditViewPermission: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    // Using a memoized selector that will only call the transform function if state.caseForms.caseHeaderV2 changes
    caseHeaderV2: caseHeaderV2Selector(state),
    fetchingCaseHeaderV2: state.caseForms.fetchingCaseHeaderV2,
    showAgentEdit: userHasRight(CASE_HEADER_AGENT_CODE_EDIT)(state),
    canAssign: userHasRight(ASSIGN)(state),
    FRList: state.fieldRep.fieldRepsBasicByCase,
    fetchingFRList: state.fieldRep.isFetchingFieldReps,
    isFetchingAssignableAppraisers: state.caseDetails.isFetchingAssignableAppraisers,
    assignableAppraisers: state.caseDetails.assignableAppraisers,
    changeAppraiserAssigned: PropTypes.func,
    showPublicPropertyCard: userHasRight(CASE_HEADER_RIGHT_FILTERED_VIEW)(state),
    editAndShowInspectionComments: userHasRight(CASE_HEADER_RIGHT_FILTERED_VIEW)(state),
    canViewCasePropertyHistory: userHasRight(CASE_PROPERTY_HISTORY_VIEW)(state),
    canViewCasePropertyHistoryLimited: userHasRight(CASE_PROPERTY_HISTORY_VIEW_LIMITED)(state),
    showRelatedCases: userHasRight(RELATED_CASES_VIEW)(state),
    viewCaseHistoryCard: userHasRight(CASE_HISTORY_CARD_VIEW)(state),
    viewInternalNotesInput: userHasRight(CASE_HISTORY_INTERNAL_NOTES_VIEW)(state),
    caseLinks: state.caseLinks.caseLinks,
    fetchingCaseLinks: state.caseLinks.fetchingCaseLinks,
    fieldSchedulingReview: userHasRight(FIELD_SCHEDULING_REVIEW)(state),
    fieldSchedulingManage: userHasRight(FIELD_SCHEDULING_MANAGE)(state),
    canViewCallLog: userHasRight(CASE_HISTORY_CALL_LOG_VIEW)(state),
    showAppraiserApprovalCard: userHasAnyRight(
      APPRAISAL_ASSIGNMENT_APPROVAL_EDIT,
      APPRAISAL_ASSIGNMENT_APPROVAL_VIEW,
    )(state),
    showFRScheduleView: userHasRight(CASE_FR_SCHEDULE_VIEW)(state),
    showAppraiserScheduleView: userHasRight(CASE_APPRAISER_SCHEDULE_VIEW)(state),
    internetUserId: state.user.profile.internetUserId,
    hasCaseAuditViewPermission: userHasRight(QA_CASE_AUDIT_VIEW)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getCaseHeaderV2Redux(controlNumber) {
    dispatch(getCaseHeaderV2Redux(controlNumber));
  },
  getFRList(controlNumber) {
    dispatch(getMinimalInspectorListByCase(controlNumber));
  },
  assignCasesToInspector(caseIds, number, callback = null) {
    dispatch(assignCasesToInspector("insurance", caseIds, number, callback));
  },
  assignCasesToAppraiser(caseIds, number, callback = null, caseAssignmentDetails = null) {
    dispatch(assignCasesToInspector("appraisal", caseIds, number, callback, caseAssignmentDetails));
  },
  unassignCasesFromInspector(caseIds, callback = null) {
    dispatch(unassignCases("insurance", caseIds, callback));
  },
  unassignCasesFromAppraiser(caseIds, callback = null) {
    dispatch(unassignCases("appraisal", caseIds, callback));
  },
  getAppraiserListByAppraisalId(controlNumber) {
    dispatch(getAppraiserListByAppraisalId(controlNumber));
  },
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
  makeWarningToast(text) {
    responseHandler.customToast(text, "warning", dispatch);
  },
  changeAppraiserAssigned(appraisalId, appraiserId, callback, caseAssignmentDetails = null) {
    dispatch(changeAppraiserAssigned(appraisalId, appraiserId, callback, caseAssignmentDetails));
  },
  getCaseLinks(controlNumber) {
    dispatch(getCaseLinksRedux(controlNumber));
  },
  getCaseLinksLimited(controlNumber) {
    dispatch(getCaseLinksLimitedRedux(controlNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CaseHeaderV2);
